import type {
  AvailableExtensionsTag,
  CaseRegistryEntry,
  HealthStatus,
  InstanceTokenResponse,
  Organization,
  OrgRuntimesReport,
  OrgUser,
  PaginatedResponse,
  PlatformExtensionsBundleDoc,
  PlatformExtensionsBundleHead,
  PlatformStats,
  RuntimeTargetVersion,
  RuntimeVersionEntry,
  ServiceHealth,
  TaskRecord,
} from "../types/platform";
import type {
  Client,
  ClientDeleteCensus,
  CreateClientInput,
  UpdateClientInput,
  Matter,
  CreateMatterInput,
  UpdateMatterInput,
  AssignmentRole,
  CreateAssignmentInput,
} from "@lexiun/org-admin";

/**
 * What lexserver's `/delete-census` actually returns.
 *
 * The shared `ClientDeleteCensus` declares these same P1 fields as OPTIONAL,
 * because fixtures and pre-P1 callers must stay valid against it. This type
 * re-declares them as REQUIRED, which is the honest contract for THIS
 * endpoint: lexserver always sends them. That lets the page gate on
 * `census.deletable` without a `?? false` that would silently read a missing
 * verdict as "not deletable" — or, worse, a future `?? true`.
 *
 * Why gate on `deletable` at all, rather than the classified variant:
 * `classifyClientDelete` only sees legacy-shape cases, but the server ALSO
 * refuses the org's self-client with a 403. `deletable` is computed from the
 * same predicates the DELETE enforces, so the dialog cannot disagree with the
 * route.
 */
export interface ClientDeleteCensusResponse extends ClientDeleteCensus {
  clientId: string;
  isSelfClient: boolean;
  /** Server's own verdict, from the same predicates the DELETE enforces. */
  deletable: boolean;
  liveCount: number;
  matterCount: number;
}
import type {
  FeedbackListParams,
  FeedbackRecord,
  FeedbackStatsParams,
  FeedbackStatsResponse,
} from "../types/feedback";
import type {
  OperationalLogAggregateResponse,
  OperationalLogFilter,
  OperationalLogPage,
  OperationalLogRecord,
} from "../types/operational-log";
import type { CopyCategory, LlmProvidersStatus } from "../types/llm-providers";
import type {
  SupervisorDecision,
  SupervisorDecisionStatus,
  SupervisorFinding,
  SupervisorFindingSeverity,
  SupervisorHealthResponse,
  SupervisorRun,
} from "../types/supervisor";
import type {
  BuiltPrompt,
  CreatePromptInput,
  ExecuteResult,
  PromptVersion,
  SlugSummary,
  UpdatePromptInput,
} from "../types/platform-prompts";
import type {
  LlmCredential,
  LlmCredentialKind,
  LlmModelChoice,
  LlmModelRef,
  LlmProvider,
} from "@lexiun/lx-llm-auth";
import type { PlatformLlmProfiles } from "@lexiun/lx-llm-resolver";
import type {
  CaseUploadersResult,
  ExecutionLedgerDetailResult,
  ExecutionLedgerFilter,
  ExecutionLedgerListResult,
  ExecutionLedgerStatsResult,
} from "../types/execution-ledger";
import { newTraceparent } from "./traceparent";
import { invalidateAuth } from "./lexiun-jwt";

/**
 * Re-export the platform-wide LLM entity vocabulary from `@lexiun/lx-llm-auth`
 * so LexPortal callers can `import type { LlmModelChoice } from
 * "@/lib/platform-api"` without learning the lower-level package. The
 * canonical home is the resolver guide §16 (PLANNED — see
 * `ai-docs/llm-default-model-mandatory-plan.md` §G.0).
 */
export type {
  LlmCredential,
  LlmCredentialKind,
  LlmModelChoice,
  LlmModelRef,
  LlmProvider,
  PlatformLlmProfiles,
};

/** Masked view of the resolver-unified `platformSettings.llmProfiles` doc
 *  returned by `GET /api/platform/settings/llm-profiles`. Non-secret —
 *  every field flows through unchanged from the lexserver helper's masked
 *  return type. The audit fields (setBy/setAt/lastValidated*) live
 *  alongside the resolver-canonical (schemaVersion / profiles /
 *  defaultChatProfile) trio; the lexserver helper masks nothing here
 *  because the doc carries no secrets. */
export interface PlatformLlmProfilesMasked extends PlatformLlmProfiles {
  setBy: string;
  setAt: string;
  lastValidatedAt?: string | null;
  lastValidatedOk?: boolean | null;
}

/** One profile the platform catalog reports as able to RE-DECIDE itself when
 *  pi-ai's model catalog moves underneath it. Mirrors `ProfileDriftRisk` from
 *  `@lexiun/lx-llm-resolver` (profiles/drift-risk.ts) — not imported, because
 *  LexPortal consumes it only over the wire.
 *
 *  ⚠ `message` is documented server-side as "operator-facing, safe to surface
 *  verbatim". Render it as-is; do not recompose it from the structured fields. */
export interface ProfileDriftRisk {
  profile: string;
  base: string;
  isBaseline: boolean;
  severity: "high" | "moderate";
  costSelector: "cheaper" | "frontier";
  unpinnedProviders: string[];
  partiallyPinned: boolean;
  message: string;
}

/** `GET /api/platform/settings/llm-profiles/starter` — the live built-in tiers
 *  wrapped as a storable catalog, PLUS two advisory siblings.
 *
 *  🚨 THE SHAPE IS A TRAP, AND IT HAS BEEN SPRUNG TWICE (both on 2026-08-27).
 *  `seeding` and `driftRisks` are spread FLAT alongside the catalog's own
 *  `schemaVersion`/`profiles`/`defaultChatProfile`, but `PlatformLlmProfilesSchema`
 *  is `.strict()` and `POST /` parses the raw body through it — so POSTing this
 *  object back verbatim is refused with `unrecognized_keys`. One consumer kept
 *  the advisory fields and would have broken the save; another stripped them and
 *  lost the guardrail, then hit the exact wall `seeding.missingProviders`
 *  predicts. Use `starterCatalogOnly()` below rather than picking by hand.
 *
 *  ⚠ Every `seeding` field is CATALOG-scoped, never platform-wide: they describe
 *  the template being served, not this installation. "What is seeded on this
 *  platform?" is a different question answered by
 *  `GET /api/platform/settings/seed-credentials`. */
export interface PlatformLlmProfilesStarter extends PlatformLlmProfiles {
  seeding: {
    referencedProviders: string[];
    satisfiedProviders: string[];
    missingProviders: string[];
  };
  driftRisks: ProfileDriftRisk[];
}

/** One observation about the STORED catalog, from
 *  `GET /api/platform/settings/llm-profiles/advisories`. Mirrors lexserver's
 *  `CatalogAdvisory` (services/llm-catalog-advisories.ts) — not imported,
 *  consumed over the wire.
 *
 *  🚨 `statement` and `consequence` are DESCRIPTIVE BY RULING, and lexserver
 *  regex-tests its own prose to keep them so. They state what IS and what
 *  FOLLOWS — never a verdict — because `defaultChatProfile` carries no
 *  provenance: the server cannot distinguish "never advanced" from
 *  "deliberately chosen". Render them VERBATIM. Wrapping them in
 *  problem/fix-this chrome re-adds the judgement the server deliberately
 *  refuses to make, and spends the mechanism's credibility on its first false
 *  positive.
 *
 *  ⚠ Branch on `code`, NEVER on the prose — including for styling. Styling
 *  read from text would silently re-verdict every future row lexserver adds.
 *  `DEFAULT_CHAT_PROFILE_DANGLING` is the ONE row sanctioned to read as a
 *  verdict (a default naming an absent profile cannot be anyone's working
 *  choice); everything else is neutral. */
export interface CatalogAdvisory {
  code: "DEFAULT_CHAT_PROFILE_DANGLING" | "DEFAULT_CHAT_PROFILE_NOT_CONCIERGE";
  subject: string;
  statement: string;
  consequence: string;
}

/** The advisory set — a UNION, deliberately.
 *
 *  🔑 `unknown` is a first-class arm, NOT an empty list. "This catalog raised no
 *  advisories" and "the catalog could not be read, so none could be computed"
 *  are different facts and only one means stop looking. A consumer that renders
 *  an empty array for `unknown` shows a clean bill of health for a platform
 *  nobody managed to inspect — the same unknown-collapsed-into-a-value defect
 *  this file already guards against in the editor's recovery reads. */
export type CatalogAdvisorySet =
  | { status: "known"; advisories: CatalogAdvisory[] }
  | { status: "unknown"; reason: string };

/** `GET /api/platform/settings/llm-profiles/previous` — the catalog the current
 *  one REPLACED, or `null` when it has never been overwritten.
 *
 *  Deliberately NESTED where `/starter` is flat: `catalog` is exactly the
 *  POST-able trio, so a consumer seeding a draft writes `previous.catalog` with
 *  nothing to strip. It is an UNDO, not an archive — one generation deep, and
 *  `null` on a platform whose catalog was never replaced (so it does NOT cover
 *  the virgin-platform case; `/starter` does). */
export interface PlatformLlmProfilesPrevious {
  replacedAt: string;
  replacedBy: string;
  catalog: PlatformLlmProfiles;
  audit: Record<string, unknown>;
}

/** Narrow any catalog-bearing response down to EXACTLY the three fields
 *  `POST /api/platform/settings/llm-profiles` accepts.
 *
 *  Exists because two different producers hand back a catalog with extra
 *  fields attached — `/starter` spreads advisory siblings flat, and
 *  `GET /` returns the masked superset with `setBy`/`setAt`/`lastValidated*`.
 *  Both are refused by the `.strict()` storage schema. Picking three fields is
 *  trivial; REMEMBERING to is what failed, so it lives in one named function
 *  that every seed path calls. */
export function starterCatalogOnly(
  source: PlatformLlmProfiles,
): PlatformLlmProfiles {
  return {
    schemaVersion: source.schemaVersion,
    profiles: source.profiles,
    defaultChatProfile: source.defaultChatProfile,
  };
}

/** Masked view of one per-provider seed credential (drops the encrypted
 *  blob; surfaces only kind + audit metadata). Mirrors the lexserver
 *  `PlatformSeedCredentialMasked` shape. */
/** Masked per-provider view of a SEARCH fallback credential
 *  (`searchSeedCredentials`, S5b). Mirrors `PlatformSearchCredentialPublic`
 *  on the server: metadata + audit, never the ciphertext and never a
 *  masked sentinel. A provider ABSENT from the map is unconfigured — the
 *  map never carries a placeholder row, so "missing key" and "key I could
 *  not read" stay distinguishable at the call site. */
export interface SearchSeedCredentialMasked {
  provider: string;
  credentialKind: "api-key";
  configured: true;
  setBy: string;
  setAt: string;
  lastValidatedAt?: string | null;
  lastValidatedOk?: boolean | null;
}

export interface PlatformSeedCredentialMasked {
  provider: LlmProvider;
  credentialKind: LlmCredentialKind;
  baseUrl?: string;
  headers?: Record<string, string>;
  hasCustomApiKey?: boolean;
  setBy: string;
  setAt: string;
  lastValidatedAt?: string | null;
  lastValidatedOk?: boolean | null;
}

/** Projected pi-ai model view returned by
 *  `GET /api/platform/llm-catalog/:provider`. Picker UIs use this to
 *  populate provider-aware model dropdowns without bundling the full
 *  pi-ai SDK into the SPA. */
export interface PlatformCatalogModel {
  id: string;
  contextWindow: number;
  reasoning: boolean;
  vision: boolean;
  family?: string;
  cost: {
    input?: number;
    output?: number;
  };
}

/** Masked structural view of `platformSettings.defaultLlmChoice` returned
 *  by `GET /api/platform/settings` — never carries the credential payload.
 *  Mirror of `lexserver/src/db/models/platform-settings.ts:
 *  PlatformDefaultLlmChoiceMasked` (the two MUST agree). */
export interface PlatformDefaultLlmChoiceMasked {
  ref: LlmModelRef;
  credentialKind: LlmCredentialKind;
  credentialProvider: LlmProvider;
  baseUrl?: string;
  headers?: Record<string, string>;
  hasCustomApiKey?: boolean;
  lastValidatedAt?: string | null;
  lastValidatedOk?: boolean | null;
  setAt?: string;
  setBy?: string;
}

/**
 * Raw assignment row shape returned by the Phase B lexserver routes.
 * Lexserver returns direct rows only — LexPortal / LexIDE compose
 * cascade-derived rows client-side by fetching parent scopes.
 * Dates arrive as ISO strings (JSON round-trip).
 */
export interface RawClientAssignmentRow {
  assignmentId: string;
  clientId: string;
  orgId: string;
  userId: string;
  role: AssignmentRole;
  assignedBy: string;
  assignedAt: string;
  expiresAt?: string;
  isActive: boolean;
}

export interface RawMatterAssignmentRow {
  assignmentId: string;
  matterId: string;
  clientId: string;
  orgId: string;
  userId: string;
  role: AssignmentRole;
  assignedBy: string;
  assignedAt: string;
  expiresAt?: string;
  isActive: boolean;
}

export class PlatformApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    /**
     * Optional structured payload from the server's error envelope —
     * any extra fields beyond `{code, message}` (e.g. the
     * `LLM_VALIDATION_FAILED` 422's `validation: {ok:false, code,
     * message, latencyMs}`). Preserved verbatim so UI consumers can
     * render typed error states without re-parsing free-form strings.
     */
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "PlatformApiError";
  }
}

export class PlatformApi {
  constructor(
    private baseUrl: string,
    private getToken: () => Promise<string | null>,
  ) {}

  /** Expose the token getter for use by shared packages (e.g., @lexiun/org-admin). */
  async resolveToken(): Promise<string | null> {
    return this.getToken();
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    /** Extra request headers. Used for `If-Match` preconditions; see
     *  `setPlatformLlmProfiles`. Kept optional so no existing caller changes. */
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const token = await this.getToken();
    const headers: Record<string, string> = {
      // Fresh W3C trace per request so lexserver's sentry-scope
      // middleware shares one trace_id with the call's downstream
      // hops (lexruntime forwarder, harn-service). See
      // lexserver/src/middleware/sentry-scope.ts.
      traceparent: newTraceparent(),
    };
    if (body) headers["Content-Type"] = "application/json";
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (extraHeaders) Object.assign(headers, extraHeaders);

    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    // A body that is not JSON at all is a REAL, reachable case, not a
    // pathology: an unmatched route falls through to Hono's default handler,
    // which answers `text/plain` "404 Not Found". Parsing that threw a raw
    // SyntaxError, so callers saw a parse failure instead of the status —
    // and could not tell "this endpoint does not exist on this server" from
    // "the server is broken". Surface it as the typed error it is, keeping
    // the status intact. Success bodies are left alone: an unparseable 2xx
    // is a genuine surprise and should still throw loudly.
    let json: {
      ok?: boolean;
      data?: T;
      // Widened from `{code, message}` so server-supplied extra fields
      // (e.g. the LLM_VALIDATION_FAILED 422's typed `validation`
      // payload) survive into PlatformApiError.details. Routes that only
      // return {code, message} keep working unchanged — extras default
      // to undefined.
      error?: { code: string; message: string } & Record<string, unknown>;
    };
    try {
      json = await res.json();
    } catch (parseErr) {
      if (res.ok) throw parseErr;
      throw new PlatformApiError(
        res.status === 404 ? "ROUTE_NOT_FOUND" : "NON_JSON_RESPONSE",
        `${res.status} ${res.statusText || "error"} (non-JSON response from ${path})`,
        res.status,
      );
    }
    if (!json.ok) {
      const err = json.error ?? { code: "UNKNOWN", message: res.statusText };
      // Session-recovery: a 401 means the Lexiun JWT is dead. Hard-
      // invalidate (clears the in-memory cache AND the stale localStorage
      // seed that otherwise loops forever) so the NEXT call re-exchanges
      // fresh, and signal the recovery banner. Self-heals silently if the
      // Clerk session is still valid; otherwise the banner offers re-auth.
      if (res.status === 401) {
        invalidateAuth();
      }
      // Pull every server-supplied field beyond {code, message} into
      // `details`. Empty object becomes undefined so consumers can
      // simply `if (err.details)` rather than checking emptiness.
      const { code: _code, message: _message, ...rest } = err;
      void _code;
      void _message;
      const details = Object.keys(rest).length > 0 ? rest : undefined;
      throw new PlatformApiError(err.code, err.message, res.status, details);
    }
    return json as unknown as T;
  }

  // -- Platform endpoints --

  async listOrgs(params?: {
    status?: string;
    limit?: number;
    cursor?: string;
  }): Promise<PaginatedResponse<Organization>> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.cursor) qs.set("cursor", params.cursor);
    const q = qs.toString();
    return this.request("GET", `/api/platform/orgs${q ? `?${q}` : ""}`);
  }

  async getOrg(orgId: string): Promise<{ ok: true; data: Organization }> {
    return this.request("GET", `/api/platform/orgs/${orgId}`);
  }

  async getStats(): Promise<{ ok: true; data: PlatformStats }> {
    return this.request("GET", "/api/platform/stats");
  }

  // -- Phase 22: runtime build-version & skew (v1.1) --
  //
  // Both endpoints are CONSUMED, not owned. `runtimes/versions` is the
  // operator BATCH endpoint owned by the lexserver/LexIDE agent and may
  // not exist yet — `useRuntimeVersions` treats a 404 (or Hono's
  // default plain-text 404 → JSON-parse error) as "feature not live"
  // and hides the UI rather than erroring. `runtime/target-version`
  // ships today (lexserver platform.ts). We never compute skew here;
  // `skew` is server-authoritative.

  // `orgId` (optional) → server-side `?orgId=` filter (DB-scoped, not
  // post-filtered). Absent ⇒ all orgs (unchanged default). Results are
  // server-ordered by orgId then containerName.
  async listRuntimeVersions(
    orgId?: string,
  ): Promise<{ ok: true; data: RuntimeVersionEntry[] }> {
    const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";
    return this.request("GET", `/api/platform/runtimes/versions${qs}`);
  }

  async getRuntimeTargetVersion(): Promise<{ ok: true; data: RuntimeTargetVersion }> {
    return this.request("GET", "/api/platform/runtime/target-version");
  }

  // ── Lextools bundle broker (Phase 4.x + idle-restart Steps 1-9) ──
  // Commit C of LexPortal lextools-surfaces miniplan. Mirrors the
  // lexserver routes at lexserver/src/routes/platform-extensions.ts.

  /** GET /api/platform/extensions — current active head + history.
   *
   *  Returns 404 NO_BUNDLE_PROMOTED when no bundle has ever been
   *  promoted (cold-state). The head fields (tag, sha256, promoted_at
   *  etc.) sit at the top level of the response; the `history[]` array
   *  is also embedded — caller picks what it needs from the same call.
   *  (F4 fix 2026-06-07: previously a `getActiveExtensionsBundle()`
   *  alias pointed at `/api/platform/extensions/current` which doesn't
   *  exist. The actual route has always been root `/`. Removed the
   *  dead alias; this method is the sole accessor.) */
  /** GET /api/platform/extensions — full doc with `history[]`. */
  async getExtensionsBundleDoc(): Promise<
    { ok: true; data: PlatformExtensionsBundleDoc }
  > {
    return this.request("GET", "/api/platform/extensions");
  }

  /** GET /api/platform/extensions/available — backend-listed tags, most-recent first. */
  async listAvailableExtensionsTags(): Promise<
    { ok: true; data: { tags: AvailableExtensionsTag[] } }
  > {
    return this.request("GET", "/api/platform/extensions/available");
  }

  /** POST /api/platform/extensions/promote — operator promote + activate. */
  async promoteExtensionsBundle(body: {
    tag: string;
    reason?: string;
    activate?: "broker-only" | "idle-restart" | "immediate";
    sha256?: string;
    orgId?: string;
    scope?: string;
    promoted_via?: "lexportal" | "rollback";
  }): Promise<{
    ok: true;
    data: PlatformExtensionsBundleHead & {
      activation: {
        activate: "broker-only" | "idle-restart" | "immediate";
        enqueued: number;
        skipped: number;
      };
    };
  }> {
    return this.request("POST", "/api/platform/extensions/promote", body);
  }

  /**
   * Plan a runtime UPGRADE (T1 graceful, idle-deferred). dryRun mode —
   * returns the target set + a 120s-TTL `executionToken` + a
   * `confirmPhrase` that must be echoed verbatim to `executeFleetUpgrade`.
   * Mirrors `POST /api/platform/runtimes/upgrade` (plan phase) at
   * `lexserver/src/routes/platform.ts:1129`.
   *
   * `orgScope`:
   *  - `"all-orgs"` → plan upgrade for every azure-deploy runtime in the fleet
   *  - `{ orgId }` → plan upgrade for every azure-deploy runtime in ONE org
   *    (the scoped/safer-blast-radius path; used when the operator has
   *    narrowed the fleet table via the org-filter dropdown).
   *
   * The plan response includes `targets` (will-enqueue) and `skipped`
   * (docker / test-mock — T1 is azure-only per EC9). Re-plan if more than
   * 120s pass between plan and execute, or the runtime set changes
   * (`SET_CHANGED` 409).
   */
  async planFleetUpgrade(
    orgScope: "all-orgs" | { orgId: string },
    opts: { force?: boolean } = {},
  ): Promise<{
    ok: true;
    data: {
      phase: "plan";
      scope: string;
      count: number;
      targets: Array<{
        userId: string;
        orgId: string;
        containerName: string;
        deployTarget: string;
      }>;
      skipped: Array<{
        userId: string;
        orgId: string;
        containerName: string;
        deployTarget: string;
        reason: string;
      }>;
      confirmPhrase: string;
      executionToken: string;
      tokenTtlMs: number;
    };
  }> {
    const body =
      orgScope === "all-orgs"
        ? { scope: "all-orgs" as const, dryRun: true, ...(opts.force ? { force: true } : {}) }
        : { orgId: orgScope.orgId, dryRun: true, ...(opts.force ? { force: true } : {}) };
    return this.request("POST", "/api/platform/runtimes/upgrade", body);
  }

  /**
   * Execute a previously-planned fleet UPGRADE. Caller must echo the
   * plan-returned `executionToken` + `confirmPhrase` verbatim and provide
   * a non-empty `reason` for the audit trail. Token TTL is 120s; outside
   * it the backend returns `TOKEN_EXPIRED` → re-plan. Single-flight: a
   * second concurrent execute returns 409 `UPGRADE_IN_PROGRESS`.
   * Mirrors `POST /api/platform/runtimes/upgrade` (execute phase) — see
   * `planFleetUpgrade` for `orgScope` semantics.
   */
  async executeFleetUpgrade(
    orgScope: "all-orgs" | { orgId: string },
    executionToken: string,
    confirmPhrase: string,
    reason: string,
    opts: { force?: boolean } = {},
  ): Promise<{
    ok: true;
    data: {
      phase: "execute";
      scope: string;
      reason: string;
      enqueued: number;
      skipped: number;
    };
  }> {
    const body =
      orgScope === "all-orgs"
        ? {
            scope: "all-orgs" as const,
            executionToken,
            confirm: confirmPhrase,
            reason,
            ...(opts.force ? { force: true } : {}),
          }
        : {
            orgId: orgScope.orgId,
            executionToken,
            confirm: confirmPhrase,
            reason,
            ...(opts.force ? { force: true } : {}),
          };
    return this.request("POST", "/api/platform/runtimes/upgrade", body);
  }

  /**
   * Plan a per-RUNTIME (per-user) UPGRADE. dryRun mode — returns the
   * single-element target list + a 120s-TTL `executionToken` + a
   * `confirmPhrase` that must be echoed verbatim to
   * `executeRuntimeUpgrade`. Mirrors the per-user branch of
   * `POST /api/platform/runtimes/upgrade` (added 2026-05-20 alongside the
   * fleet-scope branch — see `lexserver/src/routes/platform.ts`
   * `RuntimeUserUpgradeSchema`). `force: true` causes the worker to
   * skip the idle-gate and roll immediately (effectively T2 semantics);
   * the audit verb on outcome will be `t1-forced`.
   */
  async planRuntimeUpgrade(
    userId: string,
    opts: { force?: boolean } = {},
  ): Promise<{
    ok: true;
    data: {
      phase: "plan";
      scope: string;
      count: number;
      targets: Array<{
        userId: string;
        orgId: string;
        containerName: string;
        deployTarget: string;
      }>;
      skipped: Array<{
        userId: string;
        orgId: string;
        containerName: string;
        deployTarget: string;
        reason: string;
      }>;
      confirmPhrase: string;
      executionToken: string;
      tokenTtlMs: number;
    };
  }> {
    return this.request("POST", "/api/platform/runtimes/upgrade", {
      userId,
      force: !!opts.force,
      dryRun: true,
    });
  }

  /**
   * Execute a previously-planned per-RUNTIME UPGRADE. Caller must echo
   * the plan-returned `executionToken` + `confirmPhrase` verbatim and
   * provide a non-empty `reason`. `force` must match the value used in
   * `planRuntimeUpgrade` (it's embedded in the confirmPhrase so a
   * mismatch fails CONFIRM_MISMATCH). Single-flight: a second concurrent
   * execute for the same user returns 409 `UPGRADE_IN_PROGRESS`.
   */
  async executeRuntimeUpgrade(
    userId: string,
    force: boolean,
    executionToken: string,
    confirmPhrase: string,
    reason: string,
  ): Promise<{
    ok: true;
    data: {
      phase: "execute";
      scope: string;
      reason: string;
      enqueued: number;
      skipped: number;
      force: boolean;
    };
  }> {
    return this.request("POST", "/api/platform/runtimes/upgrade", {
      userId,
      force,
      executionToken,
      confirm: confirmPhrase,
      reason,
    });
  }

  /**
   * Feature A (Repair) — plan a single-user runtime reinstate. Tier
   * restart | recreate (default) | reset. Returns the resolved target +
   * the confirm phrase (`<TIER> <containerName>`) + a 120s HMAC token to
   * echo in `executeReinstateRuntime`. 404 NOT_FOUND when the user has no
   * routing row (re-provision, not reinstate).
   */
  async planReinstateRuntime(
    userId: string,
    tier: "restart" | "recreate" | "reset",
  ): Promise<{
    ok: true;
    data: {
      phase: "plan";
      scope: string;
      target: {
        userId: string;
        orgId: string;
        orgSlug: string;
        containerName: string;
        deployTarget: string;
        port?: number;
        endpoint?: string;
      };
      tier: "restart" | "recreate" | "reset";
      confirmPhrase: string;
      executionToken: string;
      tokenTtlMs: number;
    };
  }> {
    return this.request("POST", "/api/platform/runtimes/reinstate", { userId, tier });
  }

  /**
   * Execute a planned reinstate. Echo the plan-returned `executionToken` +
   * `confirmPhrase` verbatim, provide a non-empty `reason`, and the SAME
   * `tier` (it's embedded in the confirm phrase). Single-flight: a second
   * concurrent execute returns 409 `REINSTATE_IN_PROGRESS`; a concurrent
   * upgrade on the same user returns 409 `UPGRADE_IN_PROGRESS`.
   */
  async executeReinstateRuntime(
    userId: string,
    tier: "restart" | "recreate" | "reset",
    executionToken: string,
    confirmPhrase: string,
    reason: string,
  ): Promise<{
    ok: true;
    data: {
      phase: "execute";
      outcome: {
        userId: string;
        orgId: string;
        containerName: string;
        deployTarget: string;
        status: "reinstated" | "restarted" | "failed";
        tierApplied: "restart" | "recreate" | "reset";
        detail?: string;
      };
    };
  }> {
    return this.request("POST", "/api/platform/runtimes/reinstate", {
      userId,
      tier,
      executionToken,
      confirm: confirmPhrase,
      reason,
    });
  }

  /**
   * `GET /api/agent/connect[?targetUserId=<id>]` — resolves a runtime
   * endpoint + the L1-derived per-org gateway token. Default (no
   * param) is caller-scoped. PLATFORM_ADMIN may pass `targetUserId`
   * to resolve another user's runtime (the fleet-select case — see
   * `ai-docs/lexportal-fleet-runtime-connect-handoff.md`).
   *
   * Routed through this client (rather than a bare fetch in callers)
   * so the dev `/lexserver` proxy prefix + Lexiun JWT auth are
   * handled consistently with the rest of the platform surface.
   */
  async getAgentConnect(targetUserId?: string): Promise<{
    ok: true;
    data: {
      agentUrl: string | null;
      wsUrl: string | null;
      status: string;
      gatewayToken?: string;
      gatewayTokenError?: string;
    };
  }> {
    const qs = targetUserId
      ? `?targetUserId=${encodeURIComponent(targetUserId)}`
      : "";
    return this.request("GET", `/api/agent/connect${qs}`);
  }

  // -- User feedback (Phase C — read-only browse + stats) --

  async listFeedback(
    params: FeedbackListParams = {},
  ): Promise<PaginatedResponse<FeedbackRecord>> {
    const qs = new URLSearchParams();
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.cursor) qs.set("cursor", params.cursor);
    if (params.orgId) qs.set("orgId", params.orgId);
    if (params.surface) qs.set("surface", params.surface);
    if (params.polarity) qs.set("polarity", params.polarity);
    if (params.status) qs.set("status", params.status);
    if (params.category) qs.set("category", params.category);
    if (params.q) qs.set("q", params.q);
    if (params.fromDate) qs.set("fromDate", params.fromDate);
    if (params.toDate) qs.set("toDate", params.toDate);
    const q = qs.toString();
    return this.request("GET", `/api/platform/feedback${q ? `?${q}` : ""}`);
  }

  async feedbackStats(
    params: FeedbackStatsParams,
  ): Promise<{ ok: true; data: FeedbackStatsResponse }> {
    const qs = new URLSearchParams();
    qs.set("groupBy", params.groupBy);
    if (params.orgId) qs.set("orgId", params.orgId);
    if (params.fromDate) qs.set("fromDate", params.fromDate);
    if (params.toDate) qs.set("toDate", params.toDate);
    return this.request("GET", `/api/platform/feedback/stats?${qs.toString()}`);
  }

  /**
   * Phase D — apply a triage update on a single record. Body must
   * carry at least one of `status`, `triageNotes` (server enforces).
   * `triagedBy` is set server-side from the JWT — never user-supplied.
   * Returns the full updated record.
   */
  async updateOrgFeedback(
    orgId: string,
    feedbackId: string,
    body: { status?: FeedbackRecord["status"]; triageNotes?: string },
  ): Promise<{ ok: true; data: FeedbackRecord }> {
    return this.request(
      "PATCH",
      `/api/org/${encodeURIComponent(orgId)}/feedback/${encodeURIComponent(feedbackId)}`,
      body,
    );
  }

  /**
   * Phase F.6 — download the current filter slice as CSV. Returns the
   * raw response so the caller can read it as a Blob and trigger a
   * browser download (Bearer auth means we can't use a plain anchor).
   * The query supports the same filters as `listFeedback` minus paging
   * — server caps at 10k rows.
   */
  async exportFeedbackCsv(
    params: Omit<FeedbackListParams, "limit" | "cursor"> = {},
  ): Promise<Response> {
    const qs = new URLSearchParams();
    if (params.orgId) qs.set("orgId", params.orgId);
    if (params.surface) qs.set("surface", params.surface);
    if (params.polarity) qs.set("polarity", params.polarity);
    if (params.status) qs.set("status", params.status);
    if (params.category) qs.set("category", params.category);
    if (params.q) qs.set("q", params.q);
    if (params.fromDate) qs.set("fromDate", params.fromDate);
    if (params.toDate) qs.set("toDate", params.toDate);
    const q = qs.toString();
    const token = await this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(
      `${this.baseUrl}/api/platform/feedback/export.csv${q ? `?${q}` : ""}`,
      { method: "GET", headers },
    );
    if (!res.ok) {
      throw new PlatformApiError(
        "EXPORT_FAILED",
        `CSV export returned ${res.status}`,
        res.status,
      );
    }
    return res;
  }

  // -- Org-scoped user listing (used by RuntimeSelector) --

  async listOrgUsers(
    orgId: string,
    params?: { status?: string; role?: string; limit?: number; cursor?: string },
  ): Promise<PaginatedResponse<OrgUser>> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.role) qs.set("role", params.role);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.cursor) qs.set("cursor", params.cursor);
    const q = qs.toString();
    return this.request("GET", `/api/org/${orgId}/users${q ? `?${q}` : ""}`);
  }

  // -- Instance token --

  async createInstanceToken(req: {
    orgId: string;
    targetUserId: string;
    adminName?: string;
    ttl?: number;
  }): Promise<{ ok: true; data: InstanceTokenResponse }> {
    return this.request("POST", "/api/platform/instance-token", req);
  }

  // -- Instance assignment --

  async assignInstance(
    userId: string,
    req: { containerEndpoint: string | null; activate?: boolean },
  ): Promise<{ ok: true; data: OrgUser }> {
    return this.request("POST", `/api/platform/users/${userId}/assign-instance`, req);
  }

  // -- Org lifecycle --

  async createOrg(req: {
    name: string;
    slug: string;
    tier?: string;
    // v1.0 localization — BCP-47 locale, ISO 3166-1 alpha-2 jurisdiction,
    // IANA timezone, ISO 4217 currency. Optional on the wire; lexserver
    // applies the Brazilian defaults (pt-BR / BR / America/Sao_Paulo / BRL)
    // when absent. Wizard sends them explicitly so the audit log records
    // the choice even when it matches the default.
    locale?: string;
    jurisdiction?: string;
    timezone?: string;
    currency?: string;
    // S5 of the shared-storage plan. "dedicated" = per-org Premium NFS
    // share (R$130/mo floor per org, strong per-share isolation); "shared"
    // = pool share + kernel-enforced subPath isolation. Wizard defaults
    // to "shared" for new orgs; the lexserver CreateOrgSchema defaults to
    // "dedicated" if absent (back-compat). The field is CREATE-only — it
    // is immutable post-provision (UpdateOrgSchema rejects it). See
    // ai-docs/lexiun-azure-storage.md.
    storageMode?: "dedicated" | "shared";
    // §8.10 C3 (R-17) — copy the source org's LLM credentials (the encrypted
    // runtimeConfig.llm map, dynamic credentials table included) VERBATIM
    // into the new org at provision time, REPLACING the platform seed. The
    // server validates the source (exists, live, has credentials) before
    // provisioning starts; the pipeline audit-logs
    // `org.credentials.copied-from`.
    copyLlmCredentialsFromOrgId?: string;
  }): Promise<{ ok: true; data: { taskId: string; orgId: string; status: string } }> {
    return this.request("POST", "/api/platform/orgs", req);
  }

  async updateOrg(orgId: string, patch: { name?: string; status?: string; settings?: { tier?: string; maxUsers?: number }; allowOrgAdminInvitations?: boolean }): Promise<{ ok: true; data: Organization }> {
    return this.request("PATCH", `/api/platform/orgs/${orgId}`, patch);
  }

  // -- Org soft-delete / reinstate / permanent-delete (slice 3, 2026-05-19).
  //    See `ai-docs/deleted-orgs-reinstate-handoff.md` for the contract.
  //    Replaces the pre-slice-3 `deleteOrg()` (which terminally flipped
  //    status to TERMINATED + orphaned containers) with a recoverable
  //    soft-delete pipeline (DELETED → reinstate → ACTIVE  OR  DELETED
  //    → permanent-delete → TERMINATED + tombstone removal).

  /**
   * Soft-delete an org — flips ACTIVE|SUSPENDED → DELETED, stops every
   * runtime + tombstones it (recoverable via `reinstateOrg`).
   * `reason` is mandatory (3-500 chars; server rejects 400 otherwise).
   */
  async deleteOrg(
    orgId: string,
    reason: string,
  ): Promise<{
    ok: true;
    data: {
      orgId: string;
      status: "DELETED";
      reason: string;
      runtimes: OrgRuntimesReport<"softDeleted">;
    };
  }> {
    return this.request("DELETE", `/api/platform/orgs/${orgId}`, { reason });
  }

  /** Reinstate a soft-deleted org → DELETED→ACTIVE + restart every soft-stopped runtime. */
  async reinstateOrg(
    orgId: string,
    reason: string,
  ): Promise<{
    ok: true;
    data: {
      orgId: string;
      status: "ACTIVE";
      reason: string;
      runtimes: OrgRuntimesReport<"reinstated">;
    };
  }> {
    return this.request(
      "POST",
      `/api/platform/orgs/${orgId}/reinstate`,
      { reason },
    );
  }

  /**
   * Plan a permanent-delete (phase 1, READ-ONLY) — returns the targets
   * + `confirmPhrase` ("PERMANENTLY DELETE <slug>") + `executionToken`
   * (120s TTL) that the destructive-confirm dialog must round-trip in
   * phase 2.
   */
  async planPermanentDelete(orgId: string): Promise<{
    ok: true;
    data: {
      phase: "plan";
      orgId: string;
      slug: string;
      targetCount: number;
      targets: Array<{
        userId: string;
        containerName: string;
        deployTarget: string;
        source: "tombstone" | "routing";
      }>;
      confirmPhrase: string;
      executionToken: string;
      tokenTtlMs: number;
    };
  }> {
    return this.request("POST", `/api/platform/orgs/${orgId}/permanent-delete`, {});
  }

  /**
   * Execute the planned permanent-delete (phase 2) — requires the
   * exact `confirmPhrase` typed back + the `executionToken` from plan +
   * a `reason` (3-500 chars; audited).
   */
  async executePermanentDelete(
    orgId: string,
    body: { executionToken: string; confirm: string; reason: string },
  ): Promise<{
    ok: true;
    data: {
      phase: "execute";
      orgId: string;
      slug: string;
      status: "TERMINATED";
      reason: string;
      runtimes: OrgRuntimesReport<"deleted">;
    };
  }> {
    return this.request(
      "POST",
      `/api/platform/orgs/${orgId}/permanent-delete`,
      body,
    );
  }

  /**
   * Create the first ORG_ADMIN (or any user) for a freshly-provisioned
   * organization. Async — returns 202 with `{ taskId, userId, status, createdAt }`;
   * caller polls `getTask(taskId)` until the 7-step user-provision pipeline
   * completes (validate-org-limits → create-clerk-user → create-mongo-record →
   * deploy-aca-container → configure-routing → health-check → activate-user).
   * Used by the §9.A `<CreateOrgWizard>` Step 4 and by `org-admin/`'s Users page.
   */
  async createOrgUser(
    orgId: string,
    req: { email: string; firstName: string; lastName: string; role?: "ORG_ADMIN" | "MEMBER" },
  ): Promise<{ ok: true; data: { taskId: string; userId: string; status: string; createdAt: string } }> {
    return this.request("POST", `/api/org/${orgId}/users`, req);
  }

  /**
   * Move an ACCEPTED user's email — their Clerk sign-in identifier — in Clerk
   * and Mongo together (miniplan S1–S3, lexserver). **PLATFORM_ADMIN only.**
   *
   * NOT the same thing as correcting a still-pending invitation, which goes
   * through org-admin's `EditUserDialog` and is refused once accepted (409
   * `EMAIL_IMMUTABLE_AFTER_ACCEPT`). That refusal is deliberate and go-live §8
   * depends on it — this is the separate, deliberate path, not a relaxation.
   *
   * `ownershipMode` is REQUIRED and has no server-side default (D-1): the
   * permissive `admin-verified` mode marks the address verified with no proof
   * of ownership, so it must never be reachable by omitting a field. Only
   * `admin-verified` is implemented today; `user-verified` returns 501.
   *
   * Returns the FULL updated user plus `previousEmail` / `oldClerkAddressDeleted`,
   * so a row can be refreshed from this response alone — no follow-up GET.
   */
  async changeAcceptedUserEmail(
    orgId: string,
    userId: string,
    req: { newEmail: string; ownershipMode: "admin-verified" | "user-verified"; reason?: string },
  ): Promise<{
    ok: true;
    data: Record<string, unknown> & {
      email?: string;
      previousEmail?: string;
      oldClerkAddressDeleted?: boolean;
    };
  }> {
    return this.request(
      "POST",
      `/api/org/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}/email/change`,
      req,
    );
  }

  // -- Tasks --

  async getTask(taskId: string): Promise<{ ok: true; data: TaskRecord }> {
    return this.request("GET", `/api/tasks/${taskId}`);
  }

  async listTasks(params?: {
    status?: string;
    type?: string;
    limit?: number;
  }): Promise<PaginatedResponse<TaskRecord>> {
    const qs = new URLSearchParams();
    if (params?.status) { qs.set("status", params.status); }
    if (params?.type) { qs.set("type", params.type); }
    if (params?.limit) { qs.set("limit", String(params.limit)); }
    const q = qs.toString();
    return this.request("GET", `/api/tasks${q ? `?${q}` : ""}`);
  }

  async cancelTask(taskId: string): Promise<{ ok: true; data: TaskRecord }> {
    return this.request("POST", `/api/tasks/${taskId}/cancel`);
  }

  async retryTask(taskId: string): Promise<{ ok: true; data: TaskRecord }> {
    return this.request("POST", `/api/tasks/${taskId}/retry`);
  }

  // -- Cases --

  async listCases(orgId?: string): Promise<{ ok: true; data: CaseRegistryEntry[] }> {
    const q = orgId ? `?orgId=${orgId}` : "";
    return this.request("GET", `/api/cases${q}`);
  }

  async getCase(caseId: string): Promise<{ ok: true; data: CaseRegistryEntry }> {
    return this.request("GET", `/api/cases/${caseId}`);
  }

  // -- Health --

  async getHealth(): Promise<HealthStatus> {
    // `/health` is a public endpoint on lexserver (no JWT gate). Don't
    // await this.getToken() here — if the token cache is stale / refresh
    // is in flight, getToken() can throw and the catch upstream sets
    // data.health=null, which renders as "LexServer Unable to reach" in
    // the PlatformHealth page while `/health` itself is happily 200.
    // Surfaced 2026-06-09: prod LexPortal Health page showed degradation
    // for LexServer/MongoDB/Redis despite the bare /health endpoint
    // returning 200 with `dependencies: { mongodb: "connected", redis: "connected" }`.
    //
    // Best-effort attach the token if it's already available (helps
    // operational-logs tracing), but never let token resolution throw.
    const headers: Record<string, string> = {};
    try {
      const token = await this.getToken();
      if (token) { headers["Authorization"] = `Bearer ${token}`; }
    } catch {
      // Silent — public endpoint doesn't need auth.
    }
    const res = await fetch(`${this.baseUrl}/health`, { headers });
    return res.json() as Promise<HealthStatus>;
  }

  async getPlatformHealth(): Promise<{ ok: true; data: { services: ServiceHealth } }> {
    return this.request("GET", "/api/platform/health");
  }

  // -- LLM Providers (read-only inspection of resolver decisions) --
  //
  // Backs the LexPortal "LLM Providers" page (Phase 7 of the resolver
  // plan). Uses the existing /api/llm-providers/:orgId/status endpoint
  // — see lexserver/src/routes/llm-providers.ts.
  async getLlmProvidersStatus(
    orgId: string,
  ): Promise<{ ok: true; data: LlmProvidersStatus }> {
    return this.request("GET", `/api/llm-providers/${orgId}/status`);
  }

  /** The org-scoped ladder-aware status (effectiveTiers + providerHealth) —
   *  the AUTHORITATIVE per-alias view the LLM Providers page renders above
   *  the platform-catalog diagnostic (plan §8.9 rewire, 2026-07-13).
   *  Platform-admin JWTs are accepted by the org route. */
  async getOrgLlmStatus(
    orgId: string,
  ): Promise<{ ok: true; data: import("../types/llm-providers").OrgLlmStatusLean }> {
    return this.request("GET", `/api/org/${orgId}/runtime-config/llm-status`);
  }

  // -- Copy Provider Settings (ai-docs/lexportal-copy-provider-settings-plan.md) --

  /** Per-category presence for the copy dialog (NO secrets — presence + hints). */
  async getProviderSettingsSummary(orgId: string): Promise<{
    ok: true;
    data: {
      orgId: string;
      slug: string;
      summary: Record<CopyCategory, { present: boolean; detail?: string }>;
    };
  }> {
    return this.request("GET", `/api/platform/orgs/${orgId}/provider-settings/summary`);
  }

  /** Replicate SELECTED categories source→dest (existing dest overwrite). */
  async copyProviderSettings(
    destOrgId: string,
    body: { sourceOrgId: string; categories: CopyCategory[]; confirm: string },
  ): Promise<{
    ok: true;
    data: {
      copied: CopyCategory[];
      warnings: string[];
      reverted_available: boolean;
      dest: { orgId: string; slug: string };
    };
  }> {
    return this.request("POST", `/api/platform/orgs/${destOrgId}/provider-settings/copy`, body);
  }

  /** Undo the last copy — restore the pre-copy snapshot (D-5). */
  async revertCopyProviderSettings(
    destOrgId: string,
    confirm: string,
  ): Promise<{ ok: true; data: { reverted: boolean; dest: { orgId: string; slug: string } } }> {
    return this.request("POST", `/api/platform/orgs/${destOrgId}/provider-settings/revert-copy`, {
      confirm,
    });
  }

  // -- Platform-default LLM credential status (Phase 5 Slice 5.1 endpoint) --
  //
  // Probes the Phase 5 NEW platform KV secrets (`platform-default-openai-
  // oauth-profile` + `platform-default-openrouter-key`) and returns a
  // status-only view — NEVER the secret material. Surfaces in the
  // CreateOrgWizard (Slice 5.3) as an *informational* banner over the
  // review step; the wizard NEVER blocks Provision on the result — the
  // canonical mandatory-credential gate lives at the BullMQ pipeline
  // step (`init-org-config.assertOrgHasUsableCredential`, Phase 1).
  // PLATFORM_ADMIN scope (router-wide gate on /api/platform/*).
  async getPlatformCredentialStatus(): Promise<{
    ok: true;
    data: {
      openaiOauth: "ok" | "missing" | "expired";
      openrouter: "ok" | "missing";
    };
  }> {
    return this.request("GET", "/api/platform/settings/credential-status");
  }

  // -- Platform-default LLM preference (Phase 5 Slice 5.4 endpoints) --
  //
  // The "preference" that new orgs inherit at provision time
  // (`init-org-config.ts` reads it through `getPlatformSettings()`).
  // `defaultLlm` is NOT a credential — it's a hint that tells the
  // provisioning pipeline which Phase 5 KV secret to prefer when
  // staging an org's first credential set. The platform KV secrets
  // themselves remain stage-by-CLI; this endpoint is preference + read
  // surface only. PLATFORM_ADMIN scope.
  //
  // SLICE 1 of `ai-docs/llm-default-model-mandatory-plan.md` adds a SECOND
  // surface — `defaultLlmChoice` — the typed `LlmModelChoice` bundle
  // (provider + model + encrypted credential) that orgs inherit in full.
  // Returned alongside the legacy `defaultLlm` here; SET separately via
  // `setPlatformDefaultLlmChoice` (the validate-then-save endpoint below).
  async getPlatformSettings(): Promise<{
    ok: true;
    data: {
      defaultLlm: { provider: string; authMode: "oauth" | "api-key" };
      defaultLlmChoice?: PlatformDefaultLlmChoiceMasked | null;
      updatedAt?: string;
      updatedBy?: string;
    };
  }> {
    return this.request("GET", "/api/platform/settings");
  }

  /**
   * Partial-merge update of the platform-default LLM preference. The
   * lexserver Zod schema is strict on `authMode` ("oauth" | "api-key")
   * and free-form on `provider` (no enum — the wizard's
   * openai/anthropic/google/xai list is convention, not validation).
   * `updatedAt` / `updatedBy` are stamped server-side from the caller's
   * JWT sub; never sent by the client.
   */
  async updatePlatformSettings(patch: {
    defaultLlm?: { provider?: string; authMode?: "oauth" | "api-key" };
  }): Promise<{
    ok: true;
    data: {
      defaultLlm: { provider: string; authMode: "oauth" | "api-key" };
      updatedAt: string;
      updatedBy: string;
    };
  }> {
    return this.request("PATCH", "/api/platform/settings", patch);
  }

  /**
   * Non-secret Langfuse config for the "LLM Calls" observability blade.
   * Langfuse is a write-only export from our side (no read proxy); this
   * returns only what's needed to construct a per-trace dashboard URL
   * (`${baseUrl}/project/${projectId}/traces/${traceId}`). NEVER carries
   * the public/secret keys. `enabled === false` (dev default) OR an empty
   * `projectId` ⇒ the blade renders trace_id as plain text, not a link.
   * PLATFORM_ADMIN scope.
   */
  async getLangfuseLinkConfig(): Promise<{
    ok: true;
    data: { enabled: boolean; baseUrl: string; projectId: string };
  }> {
    return this.request("GET", "/api/platform/observability/langfuse-link-config");
  }

  /**
   * Langfuse READ proxy — fetch a trace's full prompt + completion by
   * trace id (the bodies aren't in operationalLogs, only in Langfuse).
   * `available:false` ⇒ no read creds configured; `found:false` ⇒ creds
   * OK but the trace isn't in Langfuse (export was off when the call
   * ran). `generations[]` carries each LLM observation's input/output.
   * PLATFORM_ADMIN scope.
   */
  async getLlmCallTrace(traceId: string): Promise<{
    ok: true;
    data: {
      available: boolean;
      found: boolean;
      htmlUrl?: string;
      generations: Array<{ model?: string; input?: unknown; output?: unknown }>;
    };
  }> {
    return this.request(
      "GET",
      `/api/platform/observability/llm-calls/${encodeURIComponent(traceId)}`,
    );
  }

  /**
   * Validate-then-save the platform `LlmModelChoice` (Slice 1.C of the
   * default-model mandatory plan). Server runs a real LLM ping with the
   * supplied `{ref, credential}`; on `ok` re-encrypts the credential
   * payload + persists; on failure returns 422 with the typed validation
   * outcome so the UI can render the right error icon (auth vs
   * model-unknown vs quota vs network vs schema vs unknown).
   *
   * Throws `PlatformApiError` on non-2xx responses; the
   * `LLM_VALIDATION_FAILED` 422 carries a structured
   * `error.validation = { ok: false, code, message, latencyMs }` payload —
   * surface its fields rather than rendering the bare `message`.
   *
   * Type-invariant (enforced server-side by LlmModelChoiceSchema):
   * `choice.ref.provider === choice.credential.provider`.
   */
  async setPlatformDefaultLlmChoice(
    choice: LlmModelChoice,
  ): Promise<{
    ok: true;
    data: {
      saved: PlatformDefaultLlmChoiceMasked;
      validation: { ok: true; latencyMs: number; sampleResponse: string };
    };
  }> {
    return this.request("POST", "/api/platform/settings/llm-default", choice);
  }

  // ── P3 of ai-docs/llm-resolver-unification-impl-plan.md ──────────────
  // Replaces `setPlatformDefaultLlmChoice` (which conflated profile
  // definition + credential into one bundle). Profiles and seed-credentials
  // are now independent surfaces — operators rotate one without touching
  // the other. Both go through validate-then-save; the 422 envelope carries
  // a typed `validation` field per Slice 1.E.1's `details` pattern.

  async getPlatformLlmProfiles(): Promise<{
    ok: true;
    data: PlatformLlmProfilesMasked | null;
  }> {
    return this.request("GET", "/api/platform/settings/llm-profiles");
  }

  /** Pure resolver lookup for a bare BASE (`"MED"`) or composite
   *  (`"MED:high"`, `"MAX:xhigh"`) profile name — returns the
   *  decision the resolver would make (provider + model + the
   *  thinking overlay it would apply). Used by the prompt-executor
   *  Profile dropdown to surface the resolved model inline. */
  async resolvePlatformLlmProfile(name: string): Promise<{
    ok: true;
    data: {
      name: string;
      base: string;
      provider: string;
      model: string;
      thinkingDefault: string | null;
      maxOutputTokens: number | null;
      temperature: number | null;
    };
  }> {
    return this.request(
      "GET",
      `/api/platform/settings/llm-profiles/resolve?name=${encodeURIComponent(name)}`,
    );
  }

  /**
   * Save the entire `PlatformLlmProfiles` catalog. Runs STATIC checks only
   * (Zod schema + every referenced provider must have a seedCredential +
   * every (BASE, thinking) composite must resolve through the resolver).
   * No real-LLM pings — the per-profile probe was removed because it was
   * slow (~3-12 min/save), redundant with the seedCredential one-shot, and
   * brittle to provider-API quirks. Two 422 envelopes:
   *   - `error.code = "MISSING_SEED_CREDENTIALS"` + `error.missingProviders:
   *     LlmProvider[]` when a referenced provider has no seed.
   *   - `error.code = "LLM_COMPOSITE_RESOLUTION_FAILED"` +
   *     `error.compositeChecks: [...]` when a composite name doesn't resolve
   *     or its thinking overlay doesn't match.
   */
  async setPlatformLlmProfiles(
    catalog: PlatformLlmProfiles,
    /** The `setAt` this draft was loaded from, sent as an `If-Match`
     *  precondition (lexserver `7ca1d675d`).
     *
     *  🚨 The token rides a HEADER and must never move into the body: `POST /`
     *  strict-parses the body, so a `setAt`/`baseVersion` key there comes back
     *  `unrecognized_keys`. That mistake was proposed by this lane and caught in
     *  review — see the guide §7.1-nonies.
     *
     *  ⚠ OMITTING it is not a safe default. Absent header ⇒ lexserver behaves
     *  exactly as before, i.e. LAST-WRITE-WINS: a draft loaded before someone
     *  else's save silently reverts it. Pass it on every save from a loaded
     *  catalog. It is optional only so the signature stayed compatible.
     *
     *  Mismatch ⇒ 412 `CATALOG_PRECONDITION_FAILED` with `storedSetAt`,
     *  `storedSetBy`, `submitted`; nothing is written. */
    ifMatchSetAt?: string,
  ): Promise<{
    ok: true;
    data: { saved: PlatformLlmProfilesMasked };
  }> {
    return this.request(
      "POST",
      "/api/platform/settings/llm-profiles",
      catalog,
      ifMatchSetAt ? { "If-Match": ifMatchSetAt } : undefined,
    );
  }

  /** The live built-in tiers wrapped as a storable catalog, plus the
   *  `seeding` + `driftRisks` advisories.
   *
   *  Replaced a hardcoded `STARTER_CATALOG` constant in `<LlmProfilesEditor>`
   *  that had become a fourth copy of the profile defaults and drifted from
   *  them — it lacked `languageHint`, carried a different MED pin, and was
   *  missing `USERTASKS` ENTIRELY. A copy that loses a whole profile is the
   *  argument against copies.
   *
   *  🚨 The response is NOT POST-able as-is: `seeding`/`driftRisks` are flat
   *  siblings and `POST /` parses through a `.strict()` schema. Narrow with
   *  `starterCatalogOnly()` before seeding any draft. */
  async getPlatformLlmProfilesStarter(): Promise<{
    ok: true;
    data: PlatformLlmProfilesStarter;
  }> {
    return this.request("GET", "/api/platform/settings/llm-profiles/starter");
  }

  /** The catalog the current one REPLACED, or `null` if never overwritten.
   *
   *  The recovery path for a destructive save. `data.catalog` is already
   *  exactly the POST-able trio — nested rather than spread precisely so no
   *  caller has to know to strip anything. */
  async getPlatformLlmProfilesPrevious(): Promise<{
    ok: true;
    data: PlatformLlmProfilesPrevious | null;
  }> {
    return this.request("GET", "/api/platform/settings/llm-profiles/previous");
  }

  /** Promote the previous catalog back to current.
   *
   *  Re-runs the SAME static gates as `POST /` rather than trusting that a
   *  catalog which was once valid still is — seed credentials can be removed
   *  and the model catalog drifts under a pin. Throws `PlatformApiError` with
   *  `NO_PREVIOUS_CATALOG` (404) when there is nothing to restore, or
   *  `MISSING_SEED_CREDENTIALS` / `LLM_COMPOSITE_RESOLUTION_FAILED` (422) in
   *  the same envelope shape `POST /` uses. Nothing is changed on refusal. */
  async restorePlatformLlmProfilesPrevious(): Promise<{
    ok: true;
    data: PlatformLlmProfilesMasked;
  }> {
    return this.request(
      "POST",
      "/api/platform/settings/llm-profiles/previous/restore",
    );
  }

  /** Read-only observations about the STORED catalog — see `CatalogAdvisory`.
   *
   *  Deliberately a SEPARATE endpoint rather than siblings on `GET /`:
   *  advisory fields there would move the `.strict()` round-trip trap from
   *  `/starter` (fetched only when the catalog is absent) onto the everyday
   *  read-modify-write path. Two consumers hit that class within an hour on
   *  2026-08-27 and a third nearly did on 2026-08-30. */
  async getPlatformLlmProfilesAdvisories(): Promise<{
    ok: true;
    data: CatalogAdvisorySet;
  }> {
    return this.request("GET", "/api/platform/settings/llm-profiles/advisories");
  }

  async getPlatformDefaultChatProfile(): Promise<{
    ok: true;
    data: { defaultChatProfile: string | null };
  }> {
    return this.request(
      "GET",
      "/api/platform/settings/llm-profiles/default-chat-profile",
    );
  }

  /**
   * Rewires `platformSettings.llmProfiles.defaultChatProfile` to the named
   * profile. No real-LLM call — the profile was already validated at
   * catalog-save time; this endpoint only updates the pointer.
   */
  async setPlatformDefaultChatProfile(
    defaultChatProfile: string,
  ): Promise<{
    ok: true;
    data: { saved: PlatformLlmProfilesMasked };
  }> {
    return this.request(
      "POST",
      "/api/platform/settings/llm-profiles/default-chat-profile",
      { defaultChatProfile },
    );
  }

  // ── Search fallback credentials (S5b) ────────────────────────────
  // The `searchSeedCredentials` store is a SIBLING of `seedCredentials`,
  // never merged: LLM keys and search keys are different planes with
  // different rotation lifetimes. Platform-admin only.

  async getSearchSeedCredentials(): Promise<{
    ok: true;
    data: Record<string, SearchSeedCredentialMasked>;
  }> {
    return this.request("GET", "/api/platform/settings/search-credentials");
  }

  async setSearchSeedCredential(
    provider: string,
    apiKey: string,
  ): Promise<{ ok: true; data: SearchSeedCredentialMasked }> {
    return this.request("POST", `/api/platform/settings/search-credentials/${provider}`, {
      apiKey,
    });
  }

  async getPlatformSeedCredentials(): Promise<{
    ok: true;
    data: Partial<Record<LlmProvider, PlatformSeedCredentialMasked>>;
  }> {
    return this.request("GET", "/api/platform/settings/seed-credentials");
  }

  /**
   * Per-provider validate-then-save. `probeModel` is optional — when
   * omitted, the server walks: catalog-pin for this provider →
   * hard-coded FALLBACK_PROBE_BY_PROVIDER. Failure-mode response is the
   * same `details.validation` shape as the per-catalog save.
   */
  async setPlatformSeedCredential(
    provider: LlmProvider,
    credential: LlmCredential,
    probeModel?: string,
  ): Promise<{
    ok: true;
    data: {
      saved: PlatformSeedCredentialMasked;
      validation: {
        ok: true;
        latencyMs: number;
        sampleResponse: string;
        probeRef: { provider: string; model: string };
        probeSource: "supplied" | "catalog-pin" | "fallback";
      };
    };
  }> {
    const body: Record<string, unknown> = { credential };
    if (probeModel) body.probeModel = probeModel;
    return this.request(
      "POST",
      `/api/platform/settings/seed-credentials/${encodeURIComponent(provider)}`,
      body,
    );
  }

  async getPlatformLlmCatalogProviders(): Promise<{
    ok: true;
    data: { providers: string[] };
  }> {
    return this.request("GET", "/api/platform/llm-catalog/providers");
  }

  async getPlatformLlmCatalog(
    provider: LlmProvider,
  ): Promise<{
    ok: true;
    data: { provider: string; models: PlatformCatalogModel[]; hint?: string };
  }> {
    return this.request(
      "GET",
      `/api/platform/llm-catalog/${encodeURIComponent(provider)}`,
    );
  }

  // -- Clients (Phase D broadening) --

  async listClients(orgId: string): Promise<{ ok: true; data: Client[] }> {
    return this.request("GET", `/api/org/${orgId}/clients`);
  }

  async getClient(
    orgId: string,
    clientId: string,
  ): Promise<{ ok: true; data: Client }> {
    return this.request("GET", `/api/org/${orgId}/clients/${clientId}`);
  }

  async createClient(
    orgId: string,
    input: CreateClientInput,
  ): Promise<{ ok: true; data: Client }> {
    return this.request("POST", `/api/org/${orgId}/clients`, input);
  }

  async updateClient(
    orgId: string,
    clientId: string,
    patch: UpdateClientInput,
  ): Promise<{ ok: true; data: Client }> {
    return this.request("PATCH", `/api/org/${orgId}/clients/${clientId}`, patch);
  }

  async deleteClient(
    orgId: string,
    clientId: string,
  ): Promise<{ ok: true; data: { deleted: boolean } }> {
    return this.request("DELETE", `/api/org/${orgId}/clients/${clientId}`);
  }

  /**
   * Delete-impact census for one client — drives the three-variant
   * `ClientDeleteConfirm` (plan §2.4.4).
   *
   * Returns `null` ONLY when the ROUTE is absent (`ROUTE_NOT_FOUND` — the
   * text/plain 404 of an unmatched Hono route), i.e. the lexserver P1 guard
   * has not shipped to this server yet. That is emphatically NOT "no impact":
   * callers MUST treat `null` as "cannot delete safely", because deleting
   * against a pre-P1 lexserver runs the old unguarded hard DELETE and orphans
   * every referencing case row.
   *
   * A JSON `NOT_FOUND` (the client itself is gone) is deliberately NOT
   * collapsed into `null` — it propagates, because "this client no longer
   * exists" and "this server cannot answer" need different words in front of
   * an operator.
   *
   * ⚠ COUPLING this relies on: the census endpoint and the guarded DELETE
   * (unassign + legacy-shape 409) ship in the SAME lexserver source file, so
   * "census answers" implies "delete is guarded". If those two are ever split
   * across releases, this gate silently weakens and needs a version check
   * instead.
   */
  async fetchClientDeleteCensus(
    orgId: string,
    clientId: string,
  ): Promise<ClientDeleteCensusResponse | null> {
    try {
      const res = await this.request<{ ok: true; data: ClientDeleteCensusResponse }>(
        "GET",
        `/api/org/${orgId}/clients/${clientId}/delete-census`,
      );
      return res.data;
    } catch (e) {
      if (e instanceof PlatformApiError && e.code === "ROUTE_NOT_FOUND") return null;
      throw e;
    }
  }

  // -- Platform Prompt Manager (PLATFORM-WIDE; /api/platform/prompts/*) --
  // The versioned, operator-editable prompt registry. See
  // ai-docs/lexserver-prompts-implementation-plan.md.

  async listPlatformPrompts(): Promise<{
    ok: true;
    data: { prompts: SlugSummary[] };
  }> {
    return this.request("GET", `/api/platform/prompts`);
  }

  async getPromptVersions(
    slug: string,
  ): Promise<{ ok: true; data: { slug: string; versions: PromptVersion[] } }> {
    return this.request(
      "GET",
      `/api/platform/prompts/${encodeURIComponent(slug)}/versions`,
    );
  }

  async createPlatformPrompt(
    input: CreatePromptInput,
  ): Promise<{ ok: true; data: PromptVersion }> {
    return this.request("POST", `/api/platform/prompts`, input);
  }

  async updatePlatformPrompt(
    slug: string,
    input: UpdatePromptInput,
  ): Promise<{ ok: true; data: PromptVersion }> {
    return this.request(
      "PUT",
      `/api/platform/prompts/${encodeURIComponent(slug)}`,
      input,
    );
  }

  async promotePromptLabel(
    slug: string,
    label: string,
    version: number,
  ): Promise<{ ok: true; data: { slug: string; label: string; version: number } }> {
    return this.request(
      "POST",
      `/api/platform/prompts/${encodeURIComponent(slug)}/labels`,
      { label, version },
    );
  }

  async disablePlatformPrompt(
    slug: string,
  ): Promise<{ ok: true; data: { slug: string; disabled: boolean } }> {
    return this.request(
      "DELETE",
      `/api/platform/prompts/${encodeURIComponent(slug)}`,
    );
  }

  async enablePlatformPrompt(
    slug: string,
  ): Promise<{ ok: true; data: { slug: string; disabled: boolean } }> {
    return this.request(
      "POST",
      `/api/platform/prompts/${encodeURIComponent(slug)}/enable`,
    );
  }

  /** Soft-delete a prompt (recycle-bin tombstone; restorable). The audited
   *  reason is forwarded to operationalLogs only — the registry stores no
   *  reason field. */
  async softDeletePlatformPrompt(
    slug: string,
    reason: string,
  ): Promise<{
    ok: true;
    data: { slug: string; deleted: true; deletedAt: string };
  }> {
    return this.request(
      "POST",
      `/api/platform/prompts/${encodeURIComponent(slug)}/soft-delete`,
      { reason },
    );
  }

  /** Restore a previously soft-deleted prompt. Does NOT re-set a `_disabled`
   *  label that softDelete cleared — restoring to disabled is a separate
   *  step. */
  async restorePlatformPrompt(
    slug: string,
  ): Promise<{ ok: true; data: { slug: string; deleted: false } }> {
    return this.request(
      "POST",
      `/api/platform/prompts/${encodeURIComponent(slug)}/restore`,
    );
  }

  /** Validate vars + assemble the prompt server-side (the gate). */
  async buildPrompt(
    slug: string,
    vars: Record<string, unknown>,
    opts?: { version?: number; label?: string },
  ): Promise<{ ok: true; data: BuiltPrompt }> {
    return this.request(
      "POST",
      `/api/prompts/${encodeURIComponent(slug)}/build`,
      { vars, version: opts?.version, label: opts?.label },
    );
  }

  /**
   * Execute a prompt through the platform LLM-execution API. As PLATFORM_ADMIN
   * the call resolves against the PLATFORM default credentials (seedCredentials)
   * — the executor UI states "using default provider credentials". Gated on the
   * orgId=null escape hatch (see llm-execute-platform-credentials-handoff.md).
   */
  async executeLlm(input: {
    system?: string;
    profile?: string;
    messages: Array<{ role: string; content: string }>;
    maxTokens?: number;
    /** Structured originator + prompt identity → logged to operationalLogs
     *  (surfaces in the "LLM Calls" blade Origin column). */
    meta?: {
      promptSlug?: string;
      promptVersion?: number;
      origin?: {
        source?: string;
        userId?: string;
        userSlug?: string;
        orgId?: string;
        orgSlug?: string;
        harnessName?: string;
        caseId?: string;
      };
    };
  }): Promise<{ ok: true; data: ExecuteResult }> {
    return this.request("POST", `/api/llm/execute`, { ...input, stream: false });
  }

  // -- Matters (Phase D broadening) --

  async listMatters(
    orgId: string,
    clientId: string,
  ): Promise<{ ok: true; data: Matter[] }> {
    return this.request("GET", `/api/org/${orgId}/clients/${clientId}/matters`);
  }

  async getMatter(
    orgId: string,
    clientId: string,
    matterId: string,
  ): Promise<{ ok: true; data: Matter }> {
    return this.request(
      "GET",
      `/api/org/${orgId}/clients/${clientId}/matters/${matterId}`,
    );
  }

  async createMatter(
    orgId: string,
    clientId: string,
    input: CreateMatterInput,
  ): Promise<{ ok: true; data: Matter }> {
    return this.request(
      "POST",
      `/api/org/${orgId}/clients/${clientId}/matters`,
      input,
    );
  }

  async updateMatter(
    orgId: string,
    clientId: string,
    matterId: string,
    patch: UpdateMatterInput,
  ): Promise<{ ok: true; data: Matter }> {
    return this.request(
      "PATCH",
      `/api/org/${orgId}/clients/${clientId}/matters/${matterId}`,
      patch,
    );
  }

  async deleteMatter(
    orgId: string,
    clientId: string,
    matterId: string,
  ): Promise<{ ok: true; data: { deleted: boolean } }> {
    return this.request(
      "DELETE",
      `/api/org/${orgId}/clients/${clientId}/matters/${matterId}`,
    );
  }

  // -- Client-level assignments (Phase D broadening) --

  async listClientAssignments(
    orgId: string,
    clientId: string,
  ): Promise<{ ok: true; data: RawClientAssignmentRow[] }> {
    return this.request(
      "GET",
      `/api/org/${orgId}/clients/${clientId}/assignments`,
    );
  }

  async createClientAssignment(
    orgId: string,
    clientId: string,
    input: CreateAssignmentInput,
  ): Promise<{ ok: true; data: RawClientAssignmentRow }> {
    return this.request(
      "POST",
      `/api/org/${orgId}/clients/${clientId}/assignments`,
      input,
    );
  }

  async updateClientAssignment(
    orgId: string,
    clientId: string,
    userId: string,
    patch: { role?: AssignmentRole; isActive?: boolean },
  ): Promise<{ ok: true; data: RawClientAssignmentRow }> {
    return this.request(
      "PATCH",
      `/api/org/${orgId}/clients/${clientId}/assignments/${userId}`,
      patch,
    );
  }

  async deleteClientAssignment(
    orgId: string,
    clientId: string,
    userId: string,
  ): Promise<{ ok: true; data: { deactivated: boolean } }> {
    return this.request(
      "DELETE",
      `/api/org/${orgId}/clients/${clientId}/assignments/${userId}`,
    );
  }

  // -- Matter-level assignments (Phase D broadening) --

  async listMatterAssignments(
    orgId: string,
    clientId: string,
    matterId: string,
  ): Promise<{ ok: true; data: RawMatterAssignmentRow[] }> {
    return this.request(
      "GET",
      `/api/org/${orgId}/clients/${clientId}/matters/${matterId}/assignments`,
    );
  }

  async createMatterAssignment(
    orgId: string,
    clientId: string,
    matterId: string,
    input: CreateAssignmentInput,
  ): Promise<{ ok: true; data: RawMatterAssignmentRow }> {
    return this.request(
      "POST",
      `/api/org/${orgId}/clients/${clientId}/matters/${matterId}/assignments`,
      input,
    );
  }

  async updateMatterAssignment(
    orgId: string,
    clientId: string,
    matterId: string,
    userId: string,
    patch: { role?: AssignmentRole; isActive?: boolean },
  ): Promise<{ ok: true; data: RawMatterAssignmentRow }> {
    return this.request(
      "PATCH",
      `/api/org/${orgId}/clients/${clientId}/matters/${matterId}/assignments/${userId}`,
      patch,
    );
  }

  async deleteMatterAssignment(
    orgId: string,
    clientId: string,
    matterId: string,
    userId: string,
  ): Promise<{ ok: true; data: { deactivated: boolean } }> {
    return this.request(
      "DELETE",
      `/api/org/${orgId}/clients/${clientId}/matters/${matterId}/assignments/${userId}`,
    );
  }

  // -- Org runtime config (PLATFORM_ADMIN — includes security + tools) --

  async getRuntimeConfig(orgId: string, opts?: { unmask?: boolean }): Promise<{ ok: true; data: Record<string, unknown> }> {
    const qs = opts?.unmask ? "?unmask=true" : "";
    return this.request("GET", `/api/org/${orgId}/runtime-config${qs}`);
  }

  async updateRuntimeConfig(orgId: string, patch: Record<string, unknown>): Promise<{ ok: true; data: Record<string, unknown> }> {
    return this.request("PATCH", `/api/org/${orgId}/runtime-config`, patch);
  }

  // -- Operational logs (PLATFORM_ADMIN only) --
  //
  // The query endpoints all sit under `/api/platform/logs/*`. The ingest
  // endpoint at `/api/platform/logs/ingest` uses a different auth scheme
  // (derived shared secret) and is NOT consumed by this SDK — only the
  // lexruntime forwarder calls it. See lexserver-architecture.md §7.6
  // and operational-logging-plan §1.6/§1.7.

  /**
   * Build the query string from an OperationalLogFilter. Multi-select
   * `level` is joined with `,` so the server can split on commas. ISO
   * timestamps round-trip verbatim.
   */
  private buildLogQuery(filter: OperationalLogFilter): string {
    const qs = new URLSearchParams();
    if (filter.service) qs.set("service", filter.service);
    if (filter.user_id) qs.set("user_id", filter.user_id);
    if (filter.org_id) qs.set("org_id", filter.org_id);
    if (filter.container_owner_user_id)
      qs.set("container_owner_user_id", filter.container_owner_user_id);
    if (filter.container_owner_org_id)
      qs.set("container_owner_org_id", filter.container_owner_org_id);
    if (filter.subsystem) qs.set("subsystem", filter.subsystem);
    if (filter.level) {
      qs.set(
        "level",
        Array.isArray(filter.level) ? filter.level.join(",") : filter.level,
      );
    }
    if (filter.trace_id) qs.set("trace_id", filter.trace_id);
    if (filter.from) qs.set("from", filter.from);
    if (filter.to) qs.set("to", filter.to);
    if (filter.q) qs.set("q", filter.q);
    if (filter.cursor) qs.set("cursor", filter.cursor);
    if (filter.limit) qs.set("limit", String(filter.limit));
    return qs.toString();
  }

  async listOperationalLogs(
    filter: OperationalLogFilter = {},
  ): Promise<{ ok: true; data: OperationalLogPage }> {
    const q = this.buildLogQuery(filter);
    return this.request("GET", `/api/platform/logs${q ? `?${q}` : ""}`);
  }

  async getOperationalLog(
    logId: string,
  ): Promise<{ ok: true; data: OperationalLogRecord }> {
    return this.request(
      "GET",
      `/api/platform/logs/${encodeURIComponent(logId)}`,
    );
  }

  async aggregateOperationalLogs(
    filter: OperationalLogFilter = {},
  ): Promise<{ ok: true; data: OperationalLogAggregateResponse }> {
    const q = this.buildLogQuery(filter);
    return this.request(
      "GET",
      `/api/platform/logs/aggregate${q ? `?${q}` : ""}`,
    );
  }

  /**
   * Open the live-tail stream. Returns a subscription handle the caller
   * can `close()` from a `useEffect` cleanup. The server polls Mongo
   * every `poll_ms` (defaults to 2s) and pushes new records as named
   * SSE events: `event: open`, `event: log`, `event: error`.
   *
   * Implementation note: we use `fetch()` + `ReadableStream` (not the
   * native `EventSource`) so the JWT can travel in the `Authorization`
   * header — same surface as every other PlatformApi call. EventSource
   * doesn't allow custom headers, which would force a query-param token
   * (URL-leakage hazard) or a server-side change.
   *
   * The SSE-line parser is intentionally minimal: it splits on the
   * blank-line frame separator, extracts `event:` + `data:` fields, and
   * forwards `log` events to `onRecord`. `error` events go to `onError`
   * (the server emits these on transient Mongo blips without ending the
   * stream). Anything else is ignored.
   */
  async streamOperationalLogs(
    filter: OperationalLogFilter,
    onRecord: (record: OperationalLogRecord) => void,
    onError?: (err: { message: string }) => void,
  ): Promise<{ close: () => void }> {
    const token = await this.getToken();
    const qs = this.buildLogQuery(filter);
    const url = `${this.baseUrl}/api/platform/logs/stream${
      qs ? `?${qs}` : ""
    }`;
    const controller = new AbortController();

    void (async () => {
      const headers: Record<string, string> = { Accept: "text/event-stream" };
      if (token) headers.Authorization = `Bearer ${token}`;
      let res: Response;
      try {
        res = await fetch(url, { headers, signal: controller.signal });
      } catch (err) {
        if (controller.signal.aborted) return;
        onError?.({ message: (err as Error).message });
        return;
      }
      if (!res.ok || !res.body) {
        onError?.({
          message: `stream open returned ${res.status}`,
        });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const processFrame = (frame: string) => {
        let event: string | null = null;
        let data = "";
        for (const line of frame.split("\n")) {
          if (line.startsWith("event: ")) event = line.slice(7).trim();
          else if (line.startsWith("data: ")) data += line.slice(6);
        }
        if (!event) return;
        if (event === "log") {
          try {
            onRecord(JSON.parse(data) as OperationalLogRecord);
          } catch {
            /* ignore malformed frames */
          }
        } else if (event === "error" && onError) {
          try {
            onError(JSON.parse(data) as { message: string });
          } catch {
            onError({ message: "stream error (unparseable)" });
          }
        }
        // `open` and any other event types are no-ops here.
      };

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx;
          // SSE frames are separated by `\n\n`.
          while ((idx = buffer.indexOf("\n\n")) >= 0) {
            const frame = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            if (frame.trim()) processFrame(frame);
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          onError?.({ message: (err as Error).message });
        }
      }
    })();

    return {
      close: () => controller.abort(),
    };
  }

  // -- Lexsup (platform-supervisor) — PLATFORM_ADMIN proxy --
  //
  // The "Supervisor" pages call lexserver's `/api/platform/sup/*` proxy, which
  // forwards to the lexsup container's `/api/sup/*` surface + reads its Cosmos
  // collections. See `lexserver/src/routes/supervisor.ts` +
  // ai-docs/lexsup-implementation-plan.md §3.7.

  /**
   * Composite supervisor health: the live `/api/sup/health` probe (with a
   * `reachable` flag), the latest run from Mongo, and the lexsup base URL
   * (which powers the launchpad's OpenClaw-dashboard link). Never errors on an
   * unreachable supervisor — the server returns `supervisor.reachable:false`.
   */
  async getSupervisorHealth(): Promise<{
    ok: true;
    data: SupervisorHealthResponse;
  }> {
    return this.request("GET", "/api/platform/sup/health");
  }

  /** Recent supervisor findings (recent-first). Optional severity / run_id filter. */
  async listSupervisorFindings(params?: {
    limit?: number;
    severity?: SupervisorFindingSeverity;
    runId?: string;
  }): Promise<{ ok: true; data: { records: SupervisorFinding[] } }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.severity) qs.set("severity", params.severity);
    if (params?.runId) qs.set("run_id", params.runId);
    const q = qs.toString();
    return this.request("GET", `/api/platform/sup/findings${q ? `?${q}` : ""}`);
  }

  /** Recent supervisor runs (recent-first). */
  async listSupervisorRuns(params?: {
    limit?: number;
  }): Promise<{ ok: true; data: { records: SupervisorRun[] } }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return this.request("GET", `/api/platform/sup/runs${q ? `?${q}` : ""}`);
  }

  /**
   * "Run audit now" — trigger a fleet-audit run on the supervisor. lexserver
   * mints the operator + platform.admin token and POSTs `/api/sup/run`,
   * returning the run summary. Throws `PlatformApiError` (503 when LEXSUP_URL
   * is unset, 502 when the supervisor is unreachable / returns an error).
   */
  async runSupervisorAudit(): Promise<{ ok: true; data: SupervisorRun }> {
    return this.request("POST", "/api/platform/sup/run");
  }

  /**
   * The supervisor decision queue (L6 — the human-in-the-loop approval gate).
   * Defaults to `status:"proposed"` (the pending queue); pass another status (or
   * `"all"`) for history. Recent-first by proposed_at.
   */
  async listSupervisorApprovals(params?: {
    limit?: number;
    status?: SupervisorDecisionStatus | "all";
  }): Promise<{ ok: true; data: { records: SupervisorDecision[] } }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.status) qs.set("status", params.status);
    const q = qs.toString();
    return this.request("GET", `/api/platform/sup/approvals${q ? `?${q}` : ""}`);
  }

  /**
   * Approve a proposed decision → lexserver stamps status:approved (+ approver)
   * and triggers execution on the supervisor (dry-run through Phase 2 — NO real
   * restart). Returns the executed decision + outcome. Throws `PlatformApiError`
   * (404 unknown id, 409 not pending, 502 supervisor unreachable / execute
   * failed).
   */
  async approveSupervisorDecision(
    decisionId: string,
  ): Promise<{ ok: true; data: { decision: SupervisorDecision; outcome?: string } }> {
    return this.request(
      "POST",
      `/api/platform/sup/decisions/${encodeURIComponent(decisionId)}/approve`,
    );
  }

  /**
   * Reject a proposed decision → lexserver stamps status:rejected (+ approver +
   * rationale). NEVER executes. Throws `PlatformApiError` (404 / 409).
   */
  async rejectSupervisorDecision(
    decisionId: string,
    rationale?: string,
  ): Promise<{ ok: true; data: { decision: SupervisorDecision } }> {
    return this.request(
      "POST",
      `/api/platform/sup/decisions/${encodeURIComponent(decisionId)}/reject`,
      rationale ? { rationale } : undefined,
    );
  }

  // -- Execution Ledger (PLATFORM_ADMIN only) --
  //
  // The centralized log of every mission + harness terminal execution
  // (lexserver `executionLedger` collection). Fed by per-user sidecar
  // forwarders (mission-service + harn-service). Self-limiting via TTL
  // (EXECUTION_LEDGER_TTL_DAYS, default 90). Consumer page:
  // `src/pages/ExecutionLedger.tsx`. Canonical contract:
  // ai-docs/lexiun-execution-ledger-plan.md §D.

  /**
   * List execution-ledger records, newest first, cursor-paginated. Filter
   * shape matches the backend's `ExecutionLedgerFilter` (`createdAt`
   * range via `from`/`to` in ms epoch).
   */
  async listExecutions(
    filter: ExecutionLedgerFilter = {},
    opts?: { limit?: number; cursor?: string },
  ): Promise<{ ok: true; data: ExecutionLedgerListResult }> {
    const qs = this.buildExecutionsQuery(filter);
    if (opts?.limit !== undefined) qs.set("limit", String(opts.limit));
    if (opts?.cursor) qs.set("cursor", opts.cursor);
    const q = qs.toString();
    return this.request(
      "GET",
      `/api/platform/executions${q ? `?${q}` : ""}`,
    );
  }

  /**
   * Detail for a single execution. For a mission, `data.leaves` carries
   * the harness executions grouped by `missionId`; for a standalone
   * harness, `data.leaves` is `[]`.
   */
  async getExecution(
    executionId: string,
  ): Promise<{ ok: true; data: ExecutionLedgerDetailResult }> {
    return this.request(
      "GET",
      `/api/platform/executions/${encodeURIComponent(executionId)}`,
    );
  }

  /**
   * Aggregate stats over the filtered window: totals (count, byStatus,
   * byKind, costUsd, tokens), per-bucket time series (count + errors),
   * and topOrgs (max 10). `bucket` defaults server-side to `"day"`.
   */
  async getExecutionStats(
    filter: ExecutionLedgerFilter = {},
    bucket?: "hour" | "day",
  ): Promise<{ ok: true; data: ExecutionLedgerStatsResult }> {
    const qs = this.buildExecutionsQuery(filter);
    if (bucket) qs.set("bucket", bucket);
    const q = qs.toString();
    return this.request(
      "GET",
      `/api/platform/executions/stats${q ? `?${q}` : ""}`,
    );
  }

  /**
   * List uploaders for a case (the distinct set of users / system writers
   * who have ever uploaded a file there). Backs the LexPortal "Filter by
   * uploader" dropdown in RuntimeFsBrowser. Operator-gated (NOT
   * platform-admin) + requires org-match — a PLATFORM_ADMIN token also
   * satisfies `requireOperator`. Path lives under `/api/org/*`, not
   * `/api/platform/*`; `request()` is path-agnostic so this Just Works.
   *
   * Canonical contract: lexserver/src/routes/org-uploaders.ts.
   */
  async listCaseUploaders(
    orgId: string,
    caseId: string,
  ): Promise<{ ok: true; data: CaseUploadersResult }> {
    return this.request(
      "GET",
      `/api/org/${encodeURIComponent(orgId)}/cases/${encodeURIComponent(
        caseId,
      )}/uploaders`,
    );
  }

  /** Shared filter → query-string builder for the executions endpoints. */
  private buildExecutionsQuery(filter: ExecutionLedgerFilter): URLSearchParams {
    const qs = new URLSearchParams();
    if (filter.orgId) qs.set("orgId", filter.orgId);
    if (filter.userId) qs.set("userId", filter.userId);
    if (filter.caseId) qs.set("caseId", filter.caseId);
    if (filter.kind) qs.set("kind", filter.kind);
    if (filter.status) qs.set("status", filter.status);
    if (filter.from !== undefined) qs.set("from", String(filter.from));
    if (filter.to !== undefined) qs.set("to", String(filter.to));
    return qs;
  }
}
